/* eslint-disable max-params */
import { cloneDeep } from 'lodash-es'
import type {
  TrackItem,
  TrackIndex,
  TrackSegment,
  TrackItemBase,
} from '../types'
import { generateTrackItemId } from './generate-id'
import type { InsertResult } from './track-insert-position'

function insert<T extends TrackItem>(
  tracks: T[],
  track: T,
  target: T,
  value: number,
): TrackIndex {
  const targetIndex = tracks.findIndex((t) => t.id === target.id)
  if (targetIndex > -1) {
    tracks.splice(targetIndex + value, 0, track)
  }

  return -1
}

export function remove<T extends { id: any }>(target: T, arr: T[]): void {
  const index = arr.findIndex((t) => t.id === target.id)
  if (index > -1) {
    arr.splice(index, 1)
  }
}

export function copyTrackItem<T extends TrackItem>(from: T): T {
  return {
    ...cloneDeep({ ...from, segments: [] }),
    id: generateTrackItemId(),
    segments: [],
  }
}

export function createTrackItem(): TrackItem {
  return {
    id: generateTrackItemId(),
    type: '',
    icons: [],
    height: 32,
    segments: [],
  }
}

export function insertAfterTrack<T extends TrackItem>(
  track: T,
  target: T,
  tracks: T[],
): TrackIndex {
  // index + 1
  return insert(tracks, track, target, 1)
}

export function insertBeforeTrack<T extends TrackItem>(
  track: T,
  target: T,
  tracks: T[],
): TrackIndex {
  // index 就是目标的index
  return insert(tracks, track, target, 0)
}

export function addTrackSegment<S extends TrackSegment, T extends TrackItem>(
  segment: S,
  track: T,
): void {
  segment.trackId = track.id
  track.segments.push(segment)
}

export function removeTrackSegment<S extends TrackSegment, T extends TrackItem>(
  segment: S,
  tracks: T[],
  options?: {
    removeTrackIfEmpty?: boolean
  },
): void {
  const { removeTrackIfEmpty = false } = options ?? {}
  const track = tracks.find((t) => t.id === segment.trackId)
  if (track) {
    remove(segment, track.segments)
    // 清除轨道的信息
    segment.trackId = ''

    if (removeTrackIfEmpty && track.segments.length === 0) {
      remove(track, tracks)
    }
  }
}

export function flatTrackSegments<T extends TrackSegment>(
  data: TrackItemBase<T>[],
): T[] {
  return data.reduce<T[]>((r, t) => [...r, ...t.segments], [])
}

export function effectInsertResult<S extends TrackSegment>(
  targetSegment: S,
  result: InsertResult,
  data: TrackItem<S>[],
  options?: {
    removeSegment?: typeof removeTrackSegment
    addSegment?: typeof addTrackSegment
    removeTrackIfEmpty?: boolean
  },
): void {
  const {
    removeTrackIfEmpty: _removeTrackIfEmpty = true,
    removeSegment = removeTrackSegment,
    addSegment = addTrackSegment,
  } = options ?? {}

  const { position: rp, track, start, frames } = result
  targetSegment.start = start
  targetSegment.frames = frames

  if (rp === 'middle') {
    if (targetSegment.trackId !== track.id) {
      // 从上一个轨道移除
      removeSegment(targetSegment, data, {
        removeTrackIfEmpty: false,
      })
      // 插入指定轨道
      addSegment(targetSegment, track)
    }
  } else {
    const fromIt = data.find((t) => t.id === targetSegment.trackId)!
    const copyIt = copyTrackItem(fromIt)

    // 从上一个轨道移除
    removeSegment(targetSegment, data, {
      removeTrackIfEmpty: false,
    })
    // 插入指定轨道
    addSegment(targetSegment, copyIt)

    const insertTrack = rp === 'top' ? insertBeforeTrack : insertAfterTrack
    insertTrack(copyIt, track, data)
  }

  if (_removeTrackIfEmpty) {
    data.forEach((t) => {
      if (t.segments.length === 0) {
        remove(t, data)
      }
    })
  }
}
